import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/api";
import {
  Paper, Typography, Grid, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, TablePagination,
  Switch, IconButton, Tooltip, Chip, Button, Box,
  Tabs, Tab, Autocomplete, InputAdornment
} from "@mui/material";
import DeleteIcon   from "@mui/icons-material/Delete";
import SearchIcon   from "@mui/icons-material/Search";
import ClearIcon    from "@mui/icons-material/Clear";

const TipoBadge = ({ tipo }) => {
  if (tipo === "puesto")  return <Chip label="By Position" color="primary" variant="outlined" size="small" />;
  if (tipo === "general") return <Chip label="General"     color="success" variant="outlined" size="small" />;
  return                         <Chip label="Optional"    color="default" variant="outlined" size="small" />;
};

const errMsg = (e) => e?.response?.data?.detail ?? "Error updating the assignment.";

const ROWS_OPTIONS = [10, 25, 50];

function BuscadorCurso({ value, onChange }) {
  return (
    <TextField
      fullWidth
      size="small"
      placeholder="Search course..."
      value={value}
      onChange={e => onChange(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
          </InputAdornment>
        ),
        endAdornment: value ? (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => onChange("")} edge="end">
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );
}

function PaginacionTabla({ count, pagina, rowsPorPagina, onPageChange, onRowsChange }) {
  return (
    <TablePagination
      component="div"
      count={count}
      page={pagina}
      onPageChange={(_, p) => onPageChange(p)}
      rowsPerPage={rowsPorPagina}
      onRowsPerPageChange={e => { onRowsChange(parseInt(e.target.value)); onPageChange(0); }}
      rowsPerPageOptions={ROWS_OPTIONS}
      labelRowsPerPage="Rows:"
      labelDisplayedRows={({ from, to, count: c }) => `${from}–${to} of ${c}`}
      sx={{ borderTop: "1px solid", borderColor: "divider" }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────
// TAB 1 — Assignment by Position
// ─────────────────────────────────────────────────────────────────
function PorPuesto({ cursos }) {
  const [puestos, setPuestos]         = useState([]);
  const [idPuesto, setIdPuesto]       = useState("");
  const [asignadas, setAsignadas]     = useState([]);
  const [busqueda, setBusqueda]       = useState("");
  const [pagina, setPagina]           = useState(0);
  const [rowsPorPagina, setRowsPorPagina] = useState(10);

  useEffect(() => {
    api.get("/puestos")
      .then(r => {
        setPuestos(r.data);
        if (r.data.length) setIdPuesto(String(r.data[0].id_puesto));
      })
      .catch(() => alert("You do not have permission to view positions."));
  }, []);

  const cargar = useCallback(() => {
    if (!idPuesto) return;
    api.get("/asignaciones", { params: { id_puesto: idPuesto } })
      .then(r => setAsignadas(r.data))
      .catch(() => alert("You do not have permission to view assignments."));
  }, [idPuesto]);

  useEffect(() => { cargar(); }, [cargar]);

  // Reset page when position or search changes
  useEffect(() => { setPagina(0); }, [idPuesto, busqueda]);

  const asignadaPorCurso = useMemo(() => {
    const m = new Map();
    asignadas.forEach(a => m.set(a.id_curso, a));
    return m;
  }, [asignadas]);

  const cursosVisibles = useMemo(
    () => cursos.filter(c => c.tipo !== "general"),
    [cursos]
  );

  const cursosFiltrados = useMemo(() => {
    const q = busqueda.toLowerCase();
    return cursosVisibles.filter(c => c.nombre_curso.toLowerCase().includes(q));
  }, [cursosVisibles, busqueda]);

  const cursosPaginados = useMemo(
    () => cursosFiltrados.slice(pagina * rowsPorPagina, (pagina + 1) * rowsPorPagina),
    [cursosFiltrados, pagina, rowsPorPagina]
  );

  const toggle = async (curso) => {
    try {
      const actual = asignadaPorCurso.get(curso.id_curso);
      if (!actual) {
        await api.post("/asignaciones", {
          id_puesto: parseInt(idPuesto),
          id_curso: curso.id_curso,
          obligatorio: curso.tipo === "puesto",
        });
      } else {
        await api.delete("/asignaciones", { params: { id_puesto: idPuesto, id_curso: curso.id_curso } });
      }
      cargar();
    } catch (e) { alert(errMsg(e)); }
  };

  const toggleObligatorio = async (curso) => {
    try {
      const actual = asignadaPorCurso.get(curso.id_curso);
      if (!actual) return;
      await api.patch("/asignaciones", {
        id_puesto: parseInt(idPuesto),
        id_curso: curso.id_curso,
        obligatorio: !actual.obligatorio,
      });
      cargar();
    } catch (e) { alert(errMsg(e)); }
  };

  return (
    <>
      {/* ── Controls ─── */}
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Grid item xs={12} sm={4} md={3}>
          <TextField select fullWidth label="Position" value={idPuesto} onChange={e => setIdPuesto(e.target.value)}>
            {puestos.map(p => <MenuItem key={p.id_puesto} value={p.id_puesto}>{p.nombre_puesto}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm>
          <BuscadorCurso value={busqueda} onChange={setBusqueda} />
        </Grid>
        <Grid item sx={{ flexShrink: 0 }}>
          <Button variant="outlined" onClick={cargar}>Refresh</Button>
        </Grid>
      </Grid>

      {/* ── Summary ─── */}
      {busqueda && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {cursosFiltrados.length} result{cursosFiltrados.length !== 1 ? "s" : ""} for "{busqueda}"
        </Typography>
      )}

      {/* ── Table ─── */}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Course</TableCell>
            <TableCell>Type</TableCell>
            <TableCell align="center">Assigned</TableCell>
            <TableCell align="center">Mandatory</TableCell>
            <TableCell align="center">Remove</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {cursosPaginados.map(c => {
            const a        = asignadaPorCurso.get(c.id_curso);
            const asignado = !!a;
            return (
              <TableRow key={c.id_curso} hover>
                <TableCell>{c.nombre_curso}</TableCell>
                <TableCell><TipoBadge tipo={c.tipo} /></TableCell>
                <TableCell align="center">
                  <Switch checked={asignado} onChange={() => toggle(c)} color="primary" />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={c.tipo === "opcional" ? "Optional courses are not mandatory" : ""}>
                    <span>
                      <Switch
                        checked={a?.obligatorio ?? false}
                        disabled={!asignado || c.tipo === "opcional"}
                        onChange={() => toggleObligatorio(c)}
                        color="secondary"
                      />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Remove assignment">
                    <span>
                      <IconButton color="error" size="small" disabled={!asignado} onClick={() => toggle(c)}>
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}

          {cursosFiltrados.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ color: "text.secondary", py: 3 }}>
                {busqueda
                  ? `No courses found matching "${busqueda}".`
                  : "No by-position or optional courses registered."}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {cursosFiltrados.length > 0 && (
        <PaginacionTabla
          count={cursosFiltrados.length}
          pagina={pagina}
          rowsPorPagina={rowsPorPagina}
          onPageChange={setPagina}
          onRowsChange={setRowsPorPagina}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// TAB 2 — Individual Assignment by Employee
// ─────────────────────────────────────────────────────────────────
function PorEmpleado({ cursos }) {
  const [empleados, setEmpleados]         = useState([]);
  const [empSel, setEmpSel]               = useState(null);
  const [asignadas, setAsignadas]         = useState([]);
  const [busqueda, setBusqueda]           = useState("");
  const [pagina, setPagina]               = useState(0);
  const [rowsPorPagina, setRowsPorPagina] = useState(10);

  useEffect(() => {
    api.get("/empleados/select")
      .then(r => setEmpleados(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const cargar = useCallback(() => {
    if (!empSel) { setAsignadas([]); return; }
    api.get("/asignaciones_empleado", { params: { id_empleado: empSel.id_empleado } })
      .then(r => setAsignadas(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAsignadas([]));
  }, [empSel]);

  useEffect(() => { cargar(); }, [cargar]);

  // Reset page when employee or search changes
  useEffect(() => { setPagina(0); }, [empSel, busqueda]);

  const asignadaMap = useMemo(() => {
    const m = new Map();
    asignadas.forEach(a => m.set(a.id_curso, a));
    return m;
  }, [asignadas]);

  const cursosVisibles = useMemo(
    () => cursos.filter(c => c.tipo !== "general"),
    [cursos]
  );

  const cursosFiltrados = useMemo(() => {
    const q = busqueda.toLowerCase();
    return cursosVisibles.filter(c => c.nombre_curso.toLowerCase().includes(q));
  }, [cursosVisibles, busqueda]);

  const cursosPaginados = useMemo(
    () => cursosFiltrados.slice(pagina * rowsPorPagina, (pagina + 1) * rowsPorPagina),
    [cursosFiltrados, pagina, rowsPorPagina]
  );

  const toggle = async (curso) => {
    if (!empSel) return;
    try {
      const params = { id_empleado: empSel.id_empleado, id_curso: curso.id_curso };
      if (asignadaMap.has(curso.id_curso)) {
        await api.delete("/asignaciones_empleado", { params });
      } else {
        await api.post("/asignaciones_empleado", null, { params: { ...params, obligatorio: false } });
      }
      cargar();
    } catch (e) { alert(errMsg(e)); }
  };

  const toggleObligatorio = async (curso) => {
    if (!empSel) return;
    const a = asignadaMap.get(curso.id_curso);
    if (!a) return;
    try {
      await api.patch("/asignaciones_empleado", null, {
        params: {
          id_empleado: empSel.id_empleado,
          id_curso: curso.id_curso,
          obligatorio: !a.obligatorio,
        },
      });
      cargar();
    } catch (e) { alert(errMsg(e)); }
  };

  return (
    <>
      {/* ── Controls ─── */}
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Grid item xs={12} sm>
          <Autocomplete
            options={empleados}
            getOptionLabel={e => e.nombre_completo ?? ""}
            value={empSel}
            onChange={(_, v) => setEmpSel(v)}
            isOptionEqualToValue={(o, v) => o.id_empleado === v.id_empleado}
            slotProps={{ popper: { sx: { width: "auto !important", minWidth: 500 } } }}
            renderInput={params => (
              <TextField
                {...params}
                label="Employee"
                fullWidth
                InputProps={{ ...params.InputProps, sx: { fontSize: "1rem", minHeight: 56 } }}
                InputLabelProps={{ ...params.InputLabelProps, sx: { fontSize: "1rem" } }}
              />
            )}
          />
        </Grid>
        <Grid item sx={{ flexShrink: 0 }}>
          <Button variant="outlined" onClick={cargar} disabled={!empSel}>Refresh</Button>
        </Grid>
      </Grid>

      {/* ── Search (always visible) ─── */}
      <Box sx={{ mb: 2 }}>
        <BuscadorCurso value={busqueda} onChange={setBusqueda} />
      </Box>

      {!empSel ? (
        <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
          Select an employee to view and manage their individually assigned courses.
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Courses individually assigned to <strong>{empSel.nombre_completo}</strong>,
              regardless of their position. General courses do not appear here because they apply to everyone automatically.
            </Typography>
            {busqueda && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {cursosFiltrados.length} result{cursosFiltrados.length !== 1 ? "s" : ""} for "{busqueda}"
              </Typography>
            )}
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Course</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="center">Assigned</TableCell>
                <TableCell align="center">Mandatory</TableCell>
                <TableCell align="center">Remove</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cursosPaginados.map(c => {
                const a        = asignadaMap.get(c.id_curso);
                const asignado = !!a;
                return (
                  <TableRow key={c.id_curso} hover>
                    <TableCell>{c.nombre_curso}</TableCell>
                    <TableCell><TipoBadge tipo={c.tipo} /></TableCell>
                    <TableCell align="center">
                      <Switch checked={asignado} onChange={() => toggle(c)} color="primary" />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={!asignado ? "Assign the course first" : ""}>
                        <span>
                          <Switch
                            checked={a?.obligatorio ?? false}
                            disabled={!asignado}
                            onChange={() => toggleObligatorio(c)}
                            color="secondary"
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Remove individual assignment">
                        <span>
                          <IconButton color="error" size="small" disabled={!asignado} onClick={() => toggle(c)}>
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}

              {cursosFiltrados.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: "text.secondary", py: 3 }}>
                    {busqueda
                      ? `No courses found matching "${busqueda}".`
                      : "No courses registered."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {cursosFiltrados.length > 0 && (
            <PaginacionTabla
              count={cursosFiltrados.length}
              pagina={pagina}
              rowsPorPagina={rowsPorPagina}
              onPageChange={setPagina}
              onRowsChange={setRowsPorPagina}
            />
          )}
        </>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────
export default function Asignaciones() {
  const [tab, setTab]       = useState(0);
  const [cursos, setCursos] = useState([]);

  useEffect(() => {
    api.get("/cursos")
      .then(r => setCursos(Array.isArray(r.data) ? r.data : []))
      .catch(() => alert("You do not have permission to view courses."));
  }, []);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>Course Assignment</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="By Position" />
        <Tab label="By Employee" />
      </Tabs>

      {tab === 0 && <PorPuesto  cursos={cursos} />}
      {tab === 1 && <PorEmpleado cursos={cursos} />}
    </Paper>
  );
}
